import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { LengthValueCentimeter } from "$src/CGS/domain/dto/core/Length/LengthValueCentimeter";
import { Mesh, Object3D, Vector2, Vector3 } from "three";
import { FrontElementMetadata } from "../Metadata/FrontMetadata";
import { BaseFrontElement } from "./BaseFrontElement";
import { IFrontElementGenerator } from "./IFrontElementGenerator";
import { FrontElementObject, MeshFrontElementObject } from "../Objects/FrontElementObject";
import { FrontFaceObject } from "../Objects/FrontFaceObject";
import { SubMesh } from "../../Meshes/SubMesh";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { EMaterialSlot } from "../../Meshes/EMaterialSlot";
import { SubMeshCollection } from "../../Meshes/SubMeshCollection";
import { EFrontElementExtension } from "$src/CGS/domain/dto/core/EFrontElement";

export abstract class BaseFrontElementGenerator extends BaseFrontElement implements IFrontElementGenerator {

    public static readonly DOOR_THICKNESS: LengthValue = new LengthValueCentimeter(2);
    public static readonly UV_TO_METER: number = 0.8;
    public static readonly METER_TO_UV: number = 1.0 / BaseFrontElementGenerator.UV_TO_METER;

    protected _doorThickness: LengthValue;
    protected size: Vector2 = new Vector2();
    protected _cornerPositions?: Vector3[];

    public get DoorThickness(): LengthValue {
        return this._doorThickness ?? BaseFrontElementGenerator.DOOR_THICKNESS;
    }

    constructor(frontElementMetadata: FrontElementMetadata, doorThickness: LengthValue) {
        super(frontElementMetadata);
        this._doorThickness = doorThickness;
    }

    get doorThickness(): LengthValue {
        return this._doorThickness || BaseFrontElementGenerator.DOOR_THICKNESS;
    }

    protected cornerPositions: Vector3[] = [];

    protected PreBuild(): void {
        this.size = new Vector2(
            LengthMaths.getLengthValue(this.frontElementMetadata.Width).meter,
            LengthMaths.getLengthValue(this.frontElementMetadata.Height).meter
        );
    
        const thicknessVector: Vector3 = new Vector3(0, 0, -this.DoorThickness.meter);
    
        // Compute the corner position (To be used for the vertice
        this.cornerPositions = [

              // Vertice facing the wall
            new Vector3(0, this.size.y, 0), 
            new Vector3(this.size.x, this.size.y, 0), 
            new Vector3(this.size.x, 0, 0), 
            new Vector3(0, 0, 0), 
    
            // Vertice facing off the cabinet
            thicknessVector.clone().addScaledVector(new Vector3(0, this.size.y, 0), 1),
            thicknessVector.clone().addScaledVector(new Vector3(this.size.x, this.size.y, 0), 1),
            thicknessVector.clone().addScaledVector(new Vector3(this.size.x, 0, 0), 1), 
            thicknessVector.clone(), 
        ];
    }

    BuildFrontElement(face: FrontFaceObject): FrontElementObject {
        const frontElement = new MeshFrontElementObject();
        frontElement.name = EFrontElementExtension.getName(this.frontElementMetadata.Type) + "_GEO";
        face.add(frontElement);

        const subMeshCollection: SubMeshCollection = new SubMeshCollection();

        // Pre-build the element
        this.PreBuild();

        // Add the front sub meshes
        subMeshCollection.merge(this.buildFront());

        // Back/Top/Bottom/Left/Right all use the door material, so we only have to generate the submesh, which can be merged here
        const subMesh: SubMesh = new SubMesh();
        subMesh.mergeMesh(this.buildBack());
        subMesh.mergeMesh(this.buildTop());
        subMesh.mergeMesh(this.buildBottom());
        subMesh.mergeMesh(this.buildLeft());
        subMesh.mergeMesh(this.buildRight());
        subMeshCollection.add(subMesh, EMaterialSlot.Door);

        // const outlineMesh: SubMesh = new SubMesh();
        // outlineMesh.mergeMesh(this.buildBackOutline());
        // outlineMesh.mergeMesh(this.buildTopOutline());
        // outlineMesh.mergeMesh(this.buildBottomOutline());
        // outlineMesh.mergeMesh(this.buildLeftOutline());
        // outlineMesh.mergeMesh(this.buildRightOutline());
        // outlineMesh.mergeMesh(this.buildFrontOutline());

        // if (!outlineMesh.empty()) subMeshCollection.add(outlineMesh, EMaterialSlot.Outline);

        frontElement.MeshInfo = subMeshCollection.createMesh(frontElement);

        this.buildHandleBones(frontElement);

        return frontElement;
    }

    protected abstract buildFront(): SubMeshCollection;

    protected buildFrontOutline(): SubMesh {
        return this.buildBoxOutline();
    }

    protected buildBoxOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[5],
            this.cornerPositions[6],
            this.cornerPositions[7],
            this.cornerPositions[4]
        ]);
    }

    protected buildTop(): SubMesh {
        return SubMesh.polygon(
            this.cornerPositions[0],
            this.cornerPositions[1],
            this.cornerPositions[5],
            this.cornerPositions[4]
        );
    }
    
    protected buildBottom(): SubMesh {
        return SubMesh.polygon(
            this.cornerPositions[2],
            this.cornerPositions[3],
            this.cornerPositions[7],
            this.cornerPositions[6]
        );
    }
    
    protected buildLeft(): SubMesh {
        return SubMesh.polygon(
            this.cornerPositions[0],
            this.cornerPositions[4],
            this.cornerPositions[7],
            this.cornerPositions[3]
        );
    }
    
    protected buildRight(): SubMesh {
        return SubMesh.polygon(
            this.cornerPositions[1],
            this.cornerPositions[2],
            this.cornerPositions[6],
            this.cornerPositions[5]
        );
    }
    
    protected buildBack(): SubMesh {
        return SubMesh.polygon(
            this.cornerPositions[1],
            this.cornerPositions[0],
            this.cornerPositions[3],
            this.cornerPositions[2]
        );
    }
    
    protected buildTopOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[0],
            this.cornerPositions[1],
            this.cornerPositions[5],
            this.cornerPositions[4]
        ]);
    }
    
    protected buildBottomOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[2],
            this.cornerPositions[3],
            this.cornerPositions[7],
            this.cornerPositions[6]
        ]);
    }
    
    protected buildLeftOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[0],
            this.cornerPositions[4],
            this.cornerPositions[7],
            this.cornerPositions[3]
        ]);
    }
    
    protected buildRightOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[1],
            this.cornerPositions[2],
            this.cornerPositions[6],
            this.cornerPositions[5]
        ]);
    }
    
    protected buildBackOutline(): SubMesh {
        return SubMesh.outlineVertices([
            this.cornerPositions[1],
            this.cornerPositions[0],
            this.cornerPositions[3],
            this.cornerPositions[2]
        ]);
    }    
}